import { Component, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ListItemRendererComponent } from 'gung-list';
import { GridViewProductCard, OrderRow, OrderService, ProductService, Product } from 'gung-standard';
import { first, forkJoin, map, mergeMap, of, Subject, Subscription, tap } from 'rxjs';
import {
  OrderRowsByDate,
  TyloDeliveryPlanningListConfigService
} from '../../services/tylo-delivery-planning-list-config.service';
import { TyloOrderRowsService } from '../../services/tylo-order-rows.service';
import { TyloDeliveryPlanningEditModalComponent } from '../tylo-delivery-planning-edit-modal/tylo-delivery-planning-edit-modal.component';
import { DateUtilService } from 'gung-common';

interface OrderRowExtended extends OrderRow {
  product?: Product;
}

@Component({
  selector: 'tylo-tylo-delivery-planning-card-list',
  templateUrl: './tylo-delivery-planning-card-list.component.html',
  styleUrls: ['./tylo-delivery-planning-card-list.component.css']
})
export class TyloDeliveryPlanningCardListComponent
  extends ListItemRendererComponent<OrderRow[]>
  implements OnInit, OnChanges, OnDestroy
{
  selectedLanguageCode: string;
  protected subscriptions: Subscription[] = [];
  protected unsubscribe: Subject<void> = new Subject();
  public mappedData: OrderRowsByDate[] = [];
  userTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  constructor(
    private translateService: TranslateService,
    protected modalService: NgbModal,
    protected tyloOrderRowsService: TyloOrderRowsService,
    protected orderService: OrderService,
    protected productService: ProductService,
    protected dateUtilService: DateUtilService
  ) {
    super();
  }

  ngOnInit() {
    this.selectedLanguageCode = this.translateService.currentLang || 'se';
    this.subscribeToRows();
  }
  public ngOnChanges() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscribeToRows();
  }
  public ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());

    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected subscribeToRows() {
    const currentDate = new Date();

    const subscription = forkJoin([
      of(this.data)]).subscribe( data => {
      const orderRows = data[0];
      const ids = Array.from(new Set(orderRows.map(o => o.productId)));
      if(ids.length!==0){
        this.productService.getProductsByIds(ids).pipe(
          first(),
        ).subscribe(products => {
          // Order Date
          const orderRowsByDate: OrderRowsByDate[] = [];
          let lastDate;
          orderRows
            .sort((a, b) => {
              const aDate = new Date(a.extra.orp.ordberlevdat);
              const bDate = new Date(b.extra.orp.ordberlevdat);
              if (aDate > bDate) {
                return 1;
              }
              if (aDate < bDate) {
                return -1;
              }
              return 0;
            })
            .forEach((orderRow: OrderRowExtended) => {
              const product = products.find(p => p.id === orderRow.productId);
              orderRow.extra._isEditable = this.isOrderRowOneDayOld(
                currentDate,
                orderRow.extra.orp.rowcreateddt || orderRow.extra.orp.orddatum
              );
              if (!!product) {
                if (!lastDate || lastDate !== orderRow.extra.orp.ordberlevdat) {
                  lastDate = orderRow.extra.orp.ordberlevdat;
                  orderRow.product = product;
                  orderRowsByDate.push({
                    date: lastDate,
                    weight: Number(product.extra.ar.artbtotvikt) * orderRow.quantity,
                    volume: orderRow.extra.orp.q_3d_volym
                      ? Number(orderRow.extra.orp.q_3d_volym)
                      : Number(product.extra.ar.artvolym) * orderRow.quantity,
                    totalSales: Number(orderRow.extra.orp.vbordradsum),
                    currency: orderRow.extra.orp.valkod || 'EUR',
                    rows: [orderRow],
                    grossWeight: orderRow.extra.orp.q_3d_bruttovikt
                      ? Number(orderRow.extra.orp.q_3d_bruttovikt)
                      : Number(product.extra.ar.artbtotvikt) * orderRow.quantity,
                    netWeight: orderRow.extra.orp.q_3d_nettovikt
                      ? Number(orderRow.extra.orp.q_3d_nettovikt)
                      : Number(product.extra.ar.artvikt) * orderRow.quantity,
                    amount: orderRow.quantity
                  });
                } else {
                  const orderRowsByDateObj = orderRowsByDate[orderRowsByDate.length - 1];
                  orderRow.product = product;
                  orderRowsByDateObj.rows.push(orderRow);
                  orderRowsByDateObj.weight += orderRow.extra.orp.q_3d_bruttovikt
                    ? Number(orderRow.extra.orp.q_3d_bruttovikt)
                    : Number(product.extra.ar.artvikt) * orderRow.quantity;
                  orderRowsByDateObj.volume += orderRow.extra.orp.q_3d_volym
                    ? Number(orderRow.extra.orp.q_3d_volym)
                    : Number(product.extra.ar.artvolym) * orderRow.quantity;
                  orderRowsByDateObj.totalSales += Number(orderRow.extra.orp.vbordradsum);
                  orderRowsByDateObj.grossWeight += orderRow.extra.orp.q_3d_bruttovikt
                    ? Number(orderRow.extra.orp.q_3d_bruttovikt)
                    : Number(product.extra.ar.artbtotvikt) * orderRow.quantity;
                  orderRowsByDateObj.netWeight += orderRow.extra.orp.q_3d_nettovikt
                    ? Number(orderRow.extra.orp.q_3d_nettovikt)
                    : Number(product.extra.ar.artvikt) * orderRow.quantity;
                  orderRowsByDateObj.amount += orderRow.quantity;
                }
              }
            });
          const sortedOrderRowsByDate = orderRowsByDate.map(element => {
            element.rows = element.rows.sort((a, b) => Number(a.orderId) - Number(b.orderId));
            return element;
          });
          this.mappedData = sortedOrderRowsByDate;
          return sortedOrderRowsByDate;
        });
      }

   
    });
    this.subscriptions.push(subscription);
  }

  openEditModal(cartRow): void {
    const modalRef = this.modalService.open(TyloDeliveryPlanningEditModalComponent, {
      backdrop: 'static',
      keyboard: false
    });
    modalRef.componentInstance.data = cartRow;
    modalRef.result.then(
      result => {
        this.updateRowData(result.data, result.applyOnOrder);
      },
      reason => {}
    );
  }

  updateRowData(cartRow, applyOnOrder): void {
    if (!!applyOnOrder) {
      const onr = cartRow.extra.orp.ordernr;
      this.orderService
        .getOrder(onr)
        .pipe(first())
        .subscribe(order => {
          const rows = [];
          for (const row of order.rows) {
            rows.push({ ordberlevdat: cartRow.extra.orp.ordberlevdat, ordradnr: row.extra.orp.ordradnr });
          }
          this.tyloOrderRowsService.setOrderRowsExtraFields(onr, rows).subscribe(payload => {
            window.location.reload();
          });
        });
    } else {
      this.tyloOrderRowsService.setOrderRowExtraFields(cartRow).subscribe(payload => {
        window.location.reload();
      });
    }
  }

  isOrderRowOneDayOld(currentDate: Date, orddatum: string) {
    const orderDate = new Date(orddatum);
    orderDate.setDate(orderDate.getDate() + 1);
    return currentDate.getTime() <= orderDate.getTime();
  }
}
